import classNames from 'classnames';
import { distances } from 'constants/distances';
import {
  Form,
  FormikConfig,
  FormikFormProps,
  FormikProvider,
  useFormik,
} from 'formik';
import { ComponentProps, FC } from 'react';
import { LatLng } from 'utils/geolocation';
import { useTranslation } from 'utils/i18n';
import { getValidator } from 'validators/venueSearch';

import Field from '../../fields/field';
import LocationField from '../../fields/locationField';
import SelectList from '../../fields/selectList';

interface Values {
  location: string;
  distance?: string;
  geolocation?: LatLng | null;
}

type OnSubmit = FormikConfig<Values>['onSubmit'];
type GetCurrentPosition = ComponentProps<
  typeof LocationField
>['getCurrentPosition'];

interface Props extends FormikFormProps {
  initialValues?: Values;
  onSubmit: OnSubmit;
  onReset?: (values: Values) => void;
  enableReset?: boolean;
  getCurrentPosition?: GetCurrentPosition;
}

const distanceOptions = Object.keys(distances).map(key => ({
  label: key,
  value: String(distances[key as keyof typeof distances]),
}));

const emptyValues: Values = {
  location: '',
  distance: '',
  geolocation: null,
};

const VenueSearchForm: FC<Props> = ({
  className: classNameProp,
  initialValues = emptyValues,
  onSubmit,
  onReset = () => null,
  enableReset = true,
  getCurrentPosition,
  ...props
}) => {
  const { t } = useTranslation('common');
  const className = classNames('venue-search-form', classNameProp);

  const formik = useFormik<Values>({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validate: getValidator(),
  });

  const { values, errors, setValues, setFieldValue } = formik;

  const handleReset = () => {
    onReset(emptyValues);
    setValues(emptyValues);
  };

  const setGeolocation = (geolocation: LatLng | null) =>
    geolocation
      ? setValues({
          location: '',
          distance: '10',
          geolocation,
        })
      : setFieldValue('geolocation', null);

  const rejectGeolocation = () =>
    setValues({
      location: '',
      distance: '',
      geolocation: null,
    });

  return (
    <FormikProvider value={formik}>
      <Form className={className} {...props}>
        <div className="field__input">
          <Field
            as={LocationField}
            name="location"
            setValueProp="setValue"
            usingGeolocation={!!values.geolocation}
            setGeolocation={setGeolocation}
            onRejectGeolocation={rejectGeolocation}
            getCurrentPosition={getCurrentPosition}
          />
          {'location' in errors && (
            <div className="error">{errors.location}</div>
          )}
        </div>
        <div className="field__input">
          <Field
            as={SelectList}
            name="distance"
            setValueProp="onChange"
            label={t('venue_search_form.fields.distance')}
            placeholder={t('venue_search_form.fields.distance')}
            options={distanceOptions}
          />
          {'distance' in errors && (
            <div className="error">{errors.distance}</div>
          )}
        </div>

        <div className="form--actions">
          <button type="submit" className="button button--secondary">
            {t('venue_search_form.submit')}
          </button>

          {enableReset && (
            <button type="submit" onClick={handleReset} className="link">
              {t('venue_search_form.reset')}
            </button>
          )}
        </div>
      </Form>
    </FormikProvider>
  );
};

export default VenueSearchForm;
